import { openSplitViewState } from '@approvalmax/ui/src/components';
import { useAtom, useSetAtom } from 'jotai';
import cloneDeep from 'lodash/cloneDeep';
import { selectors } from 'modules/common';
import { backend, domain, State } from 'modules/data';
import { useSelector } from 'modules/react-redux';
import {
    getFieldsApprovalPermissionBySystemPurpose,
    getRequestEditMode,
} from 'pages/requestForm/selectors/requestSelectors';
import { createContext, FC, useCallback, useContext, useMemo, useState } from 'react';
import { splitViewGoToBarDefaultSettings, splitViewGoToBarSettingsState } from 'shared/components';
import { ContentSplitViewMode, contentSplitViewState } from 'shared/states';

import { useSaveAndSubmitMutation } from './hooks';
import {
    XeroBillBatchPaymentRequestContextProviderProps,
    XeroBillBatchPaymentRequestContextType,
} from './XeroBillBatchPaymentForm.types';

const XeroBillBatchPaymentRequestContext = createContext<XeroBillBatchPaymentRequestContextType>(null as any);

export const XeroBillBatchPaymentRequestContextProvider: FC<XeroBillBatchPaymentRequestContextProviderProps> = (
    props
) => {
    const { children, editRequestField, request } = props;

    const [contentSplitView, setContentSplitView] = useAtom(contentSplitViewState);
    const [openSplitView, setOpenSplitView] = useAtom(openSplitViewState);
    const setSplitViewGoToBarSettings = useSetAtom(splitViewGoToBarSettingsState);

    const [billBatchPayment, setBillBatchPayment] = useState<domain.XeroBillBatchPayment>(
        (cloneDeep(request.details) as domain.XeroBillBatchPaymentDetails) || {
            paymentDate: null,
            bankAccount: null,
            narrative: null,
            details: null,
            items: [],
            id: '',
        }
    );
    const [showErrors, setShowErrors] = useState(false);
    const [billInvoiceRequestIdsWithExceededAmount, setBillInvoiceRequestIdsWithExceededAmount] = useState<string[]>(
        []
    );
    const [billInvoiceRequestIdsWithError, setBillInvoiceRequestIdsWithError] = useState<string[]>([]);

    const integration = useSelector((state: State) =>
        selectors.integration.getIntegrationByCompanyId(state, request.companyId)
    );

    const integrationCountryCode = integration?.countryCode || backend.OrganisationVersion.GLOBAL;

    const editMode = useSelector((state) => getRequestEditMode(state, request));
    const [removeLinesPermission] = useSelector((state) =>
        getFieldsApprovalPermissionBySystemPurpose(state, request, [domain.FieldSystemPurpose.XeroBatchPaymentItems])
    );
    const removeLinesDisabled = removeLinesPermission.disabled;

    const removeSelectableBillById = useCallback(
        (xeroBillInvoiceRequestId: string) => {
            const newItems = billBatchPayment.items.filter(
                (bill) => bill.xeroBillInvoiceRequestId !== xeroBillInvoiceRequestId
            );

            setBillBatchPayment((prevBillBatchPayment) => ({
                ...prevBillBatchPayment,
                items: newItems,
            }));

            editRequestField<domain.XeroBillBatchPaymentDetails>('items', newItems);
        },
        [billBatchPayment.items, editRequestField]
    );

    const changeBillInvoiceItemField = useCallback(
        (
            xeroBillInvoiceRequestId: string,
            itemField: keyof domain.PaymentItem,
            newValue: domain.PaymentItem[keyof domain.PaymentItem]
        ) => {
            const newItems = billBatchPayment.items.map((bill) => ({
                ...bill,
                [itemField]: bill.xeroBillInvoiceRequestId === xeroBillInvoiceRequestId ? newValue : bill[itemField],
            }));

            editRequestField<domain.XeroBillBatchPaymentDetails>('items', newItems);
            setBillBatchPayment((prevBillBatchPayment) => ({
                ...prevBillBatchPayment,
                items: newItems,
            }));
        },
        [billBatchPayment.items, editRequestField]
    );

    const addBills = useCallback(
        (selectableBills: domain.XeroBatchSelectableBillItem[]) => {
            const newItems = [...billBatchPayment.items, ...selectableBills.filter((bill) => bill.selected)];

            setBillBatchPayment({
                ...billBatchPayment,
                items: newItems,
            });
            editRequestField<domain.XeroBillBatchPaymentDetails>('items', newItems);
        },
        [billBatchPayment, editRequestField]
    );

    const { saveAndSubmit, isSaving } = useSaveAndSubmitMutation(
        billBatchPayment,
        request,
        setBillInvoiceRequestIdsWithExceededAmount,
        setBillInvoiceRequestIdsWithError
    );

    const setBatchPaymentAndUpdateRequestDetails = useCallback(
        (newBatchPayment: domain.XeroBillBatchPaymentDetails, propName: keyof domain.XeroBillBatchPaymentDetails) => {
            setBillBatchPayment(newBatchPayment);
            editRequestField<domain.XeroBillBatchPaymentDetails>(propName, newBatchPayment[propName]);
        },
        [editRequestField]
    );

    const clearItems = useCallback(() => {
        setBillBatchPayment((prevBillBatchPayment) => ({
            ...prevBillBatchPayment,
            items: [],
        }));

        editRequestField<domain.XeroBillBatchPaymentDetails>('items', []);

        if (
            openSplitView &&
            (contentSplitView.mode === ContentSplitViewMode.AttachmentsToBillLinkedToBatchPayment ||
                contentSplitView.mode === ContentSplitViewMode.BillLinkedToBatchPayment)
        ) {
            setOpenSplitView(false);
            setContentSplitView({ mode: ContentSplitViewMode.Empty });
            setSplitViewGoToBarSettings(splitViewGoToBarDefaultSettings);
        }
    }, [
        contentSplitView.mode,
        editRequestField,
        openSplitView,
        setContentSplitView,
        setSplitViewGoToBarSettings,
        setOpenSplitView,
    ]);

    const updateBillBatchPayment = useCallback((newBillBatchPayment: domain.XeroBillBatchPayment) => {
        setBillBatchPayment(newBillBatchPayment);
    }, []);

    const value = useMemo(
        () => ({
            billBatchPayment,
            setBillBatchPayment: setBatchPaymentAndUpdateRequestDetails,
            showErrors,
            setShowErrors,
            saveAndSubmit,
            clearItems,
            addBills,
            removeSelectableBillById,
            changeBillInvoiceItemField,
            billInvoiceRequestIdsWithError,
            billInvoiceRequestIdsWithExceededAmount,
            updateBillBatchPayment,
            isSaving,
            companyId: request.companyId,
            integrationCountryCode,
            editMode,
            removeLinesDisabled,
        }),
        [
            billBatchPayment,
            clearItems,
            saveAndSubmit,
            setBatchPaymentAndUpdateRequestDetails,
            showErrors,
            addBills,
            removeSelectableBillById,
            changeBillInvoiceItemField,
            billInvoiceRequestIdsWithError,
            billInvoiceRequestIdsWithExceededAmount,
            updateBillBatchPayment,
            isSaving,
            request.companyId,
            integrationCountryCode,
            editMode,
            removeLinesDisabled,
        ]
    );

    return (
        <XeroBillBatchPaymentRequestContext.Provider value={value}>
            {children}
        </XeroBillBatchPaymentRequestContext.Provider>
    );
};

export function useXeroBillBatchPaymentRequestContext() {
    return useContext(XeroBillBatchPaymentRequestContext);
}
